import {
  useCustomAttributionControllerGetAllModelsQuery,
  useCustomAttributionControllerBulkUpdateWeightsMutation,
} from "~/ui-rtk/api/customAttributionApi"
import { ModelType } from "~/ui-rtk/api/types/custom-attribution.types"
import Button from "~/ui-rtk/components/ui/controls/Button"
import Select from "~/ui-rtk/components/ui/controls/Select"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Formik, Form, FieldArray, Field, useFormikContext } from "formik"
import * as Yup from "yup"
import { AdChannel } from "~/ui-rtk/api/types/custom-attribution-enums"
import {
  AttributionSource,
  GoogleAnalyticsMetric,
  GoogleAdsMetric,
  TiktokMetric,
  PinterestMetric,
  MetaMetric,
  MarathonMetric,
  ShopifyMetric,
  TripleWhaleMetric,
  getMetricLabel,
  getSourceLabel,
  resolveWeightSourceForApi,
  weightSourceForForm,
  TRIPLE_WHALE_SOURCE_META,
  TRIPLE_WHALE_SOURCE_TIKTOK,
  TRIPLE_WHALE_SOURCE_PINTEREST,
  TRIPLE_WHALE_SOURCE_GOOGLE_ADS,
  TRIPLE_WHALE_SOURCE_YOUTUBE_ADS,
} from "../constants/attribution"

interface WeightEdit {
  id?: string
  modelId: string
  modelType: ModelType
  source: string
  metric: string
  weight: number
  isEdited: boolean
  errors?: WeightFormErrors
}

interface EditingWeightState {
  modelId: string
  type: ModelType
  currentValue?: number
}

interface WeightFormErrors {
  source?: string
  metric?: string
  weight?: string
}

interface WeightFormRowProps {
  index: number
}

const WeightFormRow: React.FC<WeightFormRowProps> = ({ index }) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<WeightFormValues>()
  const weight = values.weights[index]
  if (!weight) return null
  const weightErrors = errors.weights?.[index] as WeightFormErrors | undefined
  const weightTouched = touched.weights?.[index] as
    | { source?: boolean; metric?: boolean; weight?: boolean }
    | undefined

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSource = e.target.value
    void setFieldValue(`weights.${index}.source`, newSource)
    void setFieldValue(`weights.${index}.metric`, "") // Reset metric when source changes
  }

  const getAvailableSources = (): string[] => {
    if (weight.modelType === ModelType.BRAND_VALUE) {
      return [AttributionSource.MARATHON]
    }
    const baseSources = [
      AttributionSource.GOOGLE_ANALYTICS,
      AttributionSource.GOOGLE_ADS,
      AttributionSource.MARATHON,
      AttributionSource.SHOPIFY,
    ]
    const adChannel = values.adChannel
    if (adChannel === AdChannel.META) {
      return [...baseSources, AttributionSource.META, TRIPLE_WHALE_SOURCE_META]
    }
    if (adChannel === AdChannel.TIKTOK) {
      return [
        ...baseSources,
        AttributionSource.TIKTOK,
        TRIPLE_WHALE_SOURCE_TIKTOK,
      ]
    }
    if (adChannel === AdChannel.PINTEREST) {
      return [
        ...baseSources,
        AttributionSource.PINTEREST,
        TRIPLE_WHALE_SOURCE_PINTEREST,
      ]
    }
    if (adChannel === AdChannel.GOOGLE_ADS) {
      return [
        ...baseSources,
        AttributionSource.GOOGLE_ADS,
        TRIPLE_WHALE_SOURCE_GOOGLE_ADS,
      ]
    }
    if (adChannel === AdChannel.YOUTUBE) {
      return [
        ...baseSources,
        AttributionSource.YOUTUBE_ADS,
        TRIPLE_WHALE_SOURCE_YOUTUBE_ADS,
      ]
    }
    return baseSources
  }

  const getAvailableMetrics = (source: string) => {
    if (!source) return []

    // Get all used metrics for this source
    const usedMetrics = values.weights
      .filter(w => w.source === source && w.id !== weight.id)
      .map(w => w.metric)

    let availableMetrics: Array<{ label: string; value: string }> = []

    if (
      source === TRIPLE_WHALE_SOURCE_META ||
      source === TRIPLE_WHALE_SOURCE_TIKTOK ||
      source === TRIPLE_WHALE_SOURCE_PINTEREST ||
      source === TRIPLE_WHALE_SOURCE_GOOGLE_ADS ||
      source === TRIPLE_WHALE_SOURCE_YOUTUBE_ADS
    ) {
      return Object.values(TripleWhaleMetric)
        .filter(metric => !usedMetrics.includes(metric))
        .map(metric => ({
          label: getMetricLabel("TRIPLE_WHALE", metric),
          value: metric,
        }))
    }

    switch (source as AttributionSource) {
      case AttributionSource.GOOGLE_ANALYTICS:
        availableMetrics = Object.values(GoogleAnalyticsMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.GOOGLE_ADS:
        availableMetrics = Object.values(GoogleAdsMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.TIKTOK:
        availableMetrics = Object.values(TiktokMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.PINTEREST:
        availableMetrics = Object.values(PinterestMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.META:
        availableMetrics = Object.values(MetaMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.MARATHON:
        availableMetrics = Object.values(MarathonMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.SHOPIFY:
        availableMetrics = Object.values(ShopifyMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.YOUTUBE_ADS:
        availableMetrics = Object.values(TripleWhaleMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel("TRIPLE_WHALE", metric),
            value: metric,
          }))
        break
    }

    return availableMetrics
  }

  return (
    <tr className={index % 2 === 0 ? "bg-basic-blue/10" : ""}>
      <td className="px-4 py-2">
        <div className="space-y-1">
          <Field
            as={Select}
            name={`weights.${index}.source`}
            onChange={handleSourceChange}
            options={getAvailableSources().map(s => ({
              label: getSourceLabel(s),
              value: s,
            }))}
            className={
              weightErrors?.source && weightTouched?.source
                ? "border-red-500"
                : ""
            }
          />
          {weightErrors?.source && weightTouched?.source && (
            <p className="text-red-500 text-sm">{weightErrors.source}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-2">
        <div className="space-y-1">
          <Field
            as={Select}
            name={`weights.${index}.metric`}
            options={[
              { label: "Select metric...", value: "" },
              ...getAvailableMetrics(weight.source),
            ]}
            disabled={!weight.source}
            className={
              weightErrors?.metric && weightTouched?.metric
                ? "border-red-500"
                : ""
            }
          />
          {weightErrors?.metric && weightTouched?.metric && (
            <p className="text-red-500 text-sm">{weightErrors.metric}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-2">
        <div className="space-y-1">
          <Field
            type="number"
            name={`weights.${index}.weight`}
            step="0.01"
            min="0.01"
            className={`w-full p-2 rounded-md bg-basic-blue border ${
              weightErrors?.weight && weightTouched?.weight
                ? "border-red-500"
                : "border-basic-blue"
            } text-white`}
          />
          {weightErrors?.weight && weightTouched?.weight && (
            <p className="text-red-500 text-sm">{weightErrors.weight}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-2">
        <FieldArray name="weights">
          {({ remove }) => (
            <Button
              variant={{ variant: "text" }}
              size="sm"
              onClick={() => {
                if (weight.id) {
                  void setFieldValue("deletedWeightIds", [
                    ...values.deletedWeightIds,
                    weight.id,
                  ])
                }
                remove(index)
              }}
              type="button"
            >
              Remove
            </Button>
          )}
        </FieldArray>
      </td>
    </tr>
  )
}

const weightValidationSchema = Yup.object().shape({
  weights: Yup.array().of(
    Yup.object().shape({
      source: Yup.string().required("Source is required"),
      metric: Yup.string().required("Metric is required"),
      weight: Yup.number()
        .required("Weight is required")
        .min(0.01, "Weight must be greater than 0"),
    }),
  ),
})

/** Map API attribution source string to AdChannel for edit modal. */
const SOURCE_TO_AD_CHANNEL: Record<string, AdChannel> = {
  [AttributionSource.META]: AdChannel.META,
  [AttributionSource.TIKTOK]: AdChannel.TIKTOK,
  [AttributionSource.PINTEREST]: AdChannel.PINTEREST,
  [AttributionSource.GOOGLE_ADS]: AdChannel.GOOGLE_ADS,
  [AttributionSource.YOUTUBE_ADS]: AdChannel.YOUTUBE,
}

/** Infer ad channel from weight sources (for source dropdown in edit modal). */
function inferAdChannelFromWeights(
  weightEdits: WeightEdit[],
): AdChannel | null {
  if (!weightEdits[0]) return null
  const firstResolved = resolveWeightSourceForApi(
    weightSourceForForm(weightEdits[0].source, weightEdits[0].metric),
  )
  return SOURCE_TO_AD_CHANNEL[firstResolved] ?? null
}

interface WeightFormValues {
  adChannel: AdChannel | null
  weights: WeightEdit[]
  deletedWeightIds: string[]
}

interface WeightEditModalProps {
  isOpen: boolean
  onClose: () => void
  editingWeight: EditingWeightState | null
  weightEdits: WeightEdit[]
  isLoadingModelWeights: boolean
}

const WeightEditModal: React.FC<WeightEditModalProps> = ({
  isOpen,
  onClose,
  editingWeight,
  weightEdits,
}) => {
  const [bulkUpdateWeights] =
    useCustomAttributionControllerBulkUpdateWeightsMutation()
  const { refetch: refetchModels } =
    useCustomAttributionControllerGetAllModelsQuery()

  const initialValues: WeightFormValues = {
    adChannel: inferAdChannelFromWeights(weightEdits),
    weights: weightEdits.map(w => ({
      ...w,
      source: weightSourceForForm(w.source, w.metric),
    })),
    deletedWeightIds: [],
  }

  const handleSubmit = async (values: WeightFormValues) => {
    if (!editingWeight) return

    try {
      await bulkUpdateWeights({
        modelId: editingWeight.modelId,
        weights: values.weights.map(w => ({
          id: w.id,
          modelId: editingWeight.modelId,
          modelType: w.modelType,
          source: resolveWeightSourceForApi(w.source),
          metric: w.metric,
          weight: w.weight,
        })),
        deletedWeightIds: values.deletedWeightIds,
      }).unwrap()

      onClose()
      void refetchModels()
    } catch (error) {
      console.error("Failed to save weights:", error)
    }
  }

  if (!isOpen) return null

  return (
    <Dialog isOpen={isOpen} handleClose={onClose} width={800}>
      <div className="bg-basic-dark-blue border border-basic-blue p-6 rounded-md">
        <h2 className="text-xl font-semibold mb-4">
          Edit {editingWeight?.type.toLowerCase()} weights
        </h2>
        <Formik
          initialValues={initialValues}
          validationSchema={weightValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <Form className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-basic-blue">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left">Source</th>
                      <th className="px-4 py-2 text-left">Metric</th>
                      <th className="px-4 py-2 text-left">Weight</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-basic-blue">
                    <FieldArray name="weights">
                      {({ push }) => (
                        <>
                          {values.weights.map((_, index) => (
                            <WeightFormRow key={index} index={index} />
                          ))}
                          <tr>
                            <td colSpan={4} className="px-4 py-2">
                              <Button
                                type="button"
                                variant={{ variant: "text" }}
                                onClick={() =>
                                  push({
                                    modelId: editingWeight?.modelId || "",
                                    modelType:
                                      editingWeight?.type ||
                                      ModelType.DIRECT_RESPONSE,
                                    source: AttributionSource.GOOGLE_ANALYTICS,
                                    metric: "",
                                    weight: 0,
                                    isEdited: true,
                                  })
                                }
                              >
                                Add Weight
                              </Button>
                            </td>
                          </tr>
                        </>
                      )}
                    </FieldArray>
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant={{ variant: "text" }}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant={{ variant: "solid", color: "blue" }}
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Dialog>
  )
}

export default WeightEditModal
